import React, { useEffect, useState } from "react";
import {
  Col,
  Row,
  Typography,
  Layout,
  Card,
  Button,
  Table,
  Skeleton,
  Modal,
  Form,
  Input,
  Tag,
  Empty,
} from "antd";
import { FaArrowLeft, FaEdit, FaTrash, FaEye } from "react-icons/fa";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";
import { Get } from "../../config/api/get";
import { Post } from "../../config/api/post";
import { Patch } from "../../config/api/patch";
import { Delete } from "../../config/api/delete";
import { ASSOCIATE } from "../../config/constants/api";
import { CONTENT_TYPE } from "../../config/constants/index";
import "../upcoming-lessons/upcoming-lessons.css";
import "../course-management/course-management.css";

const STATUS_COLORS = {
  PENDING: "gold",
  ACTIVE: "green",
  INACTIVE: "red",
};

function AssociateManagement() {
  const navigate = useNavigate();
  const token = useSelector((state) => state.user.userToken);
  const user = useSelector((state) => state.user.userData);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [associates, setAssociates] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const canManage = Boolean(user?.hasTrainingAssociates);

  useEffect(() => {
    if (!token) {
      navigate("/signin");
      return;
    }
    if (!canManage) {
      return;
    }
    loadAssociates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, canManage]);

  const loadAssociates = async () => {
    setLoading(true);
    try {
      const response = await Get(ASSOCIATE.list, token);
      if (response?.status) {
        setAssociates(response?.data?.associates || []);
      } else {
        swal("Oops!", response?.message || "Failed to load associates", "error");
      }
    } catch (error) {
      swal("Oops!", error?.message || "Failed to load associates", "error");
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditMode(false);
    setEditingId(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditMode(true);
    setEditingId(record._id);
    form.setFieldsValue({
      firstName: record.firstName,
      lastName: record.lastName,
      email: record.email,
      position: record.position,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditMode(false);
    setEditingId(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    setSaving(true);
    try {
      if (editMode && editingId) {
        const response = await Patch(
          ASSOCIATE.update + editingId,
          token,
          values
        );
        if (response?.status) {
          swal("Success!", response?.message || "Associate updated", "success");
          closeModal();
          loadAssociates();
        } else {
          swal("Oops!", response?.message || "Update failed", "error");
        }
      } else {
        const response = await Post(
          ASSOCIATE.create,
          values,
          token,
          null,
          CONTENT_TYPE.JSON
        );
        const payload = response?.data ?? response;
        if (payload?.status) {
          swal(
            "Success!",
            payload?.message ||
              "Associate added and sent for admin approval.",
            "success"
          );
          closeModal();
          loadAssociates();
        } else {
          swal("Oops!", payload?.message || "Create failed", "error");
        }
      }
    } catch (error) {
      swal("Oops!", error?.message || "Request failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (record) => {
    swal({
      title: "Remove associate?",
      text: `${record.firstName} ${record.lastName} will be removed or deactivated.`,
      icon: "warning",
      buttons: ["Cancel", "Confirm"],
      dangerMode: true,
    }).then(async (confirmed) => {
      if (!confirmed) return;
      try {
        const response = await Delete(ASSOCIATE.delete + record._id, token);
        if (response?.status) {
          swal("Success!", response?.message || "Associate removed", "success");
          loadAssociates();
        } else {
          swal("Oops!", response?.message || "Delete failed", "error");
        }
      } catch (error) {
        swal("Oops!", error?.message || "Delete failed", "error");
      }
    });
  };

  const columns = [
    {
      title: "S. No.",
      key: "index",
      width: 80,
      render: (_, __, index) => (
        <span>{(index < 9 ? "0" : "") + (index + 1)}</span>
      ),
    },
    {
      title: "Name",
      key: "name",
      render: (_, record) => `${record.firstName} ${record.lastName}`,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Position",
      dataIndex: "position",
      key: "position",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={STATUS_COLORS[status] || "default"}>{status}</Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 140,
      render: (_, record) => (
        <div style={{ display: "flex", gap: 12 }}>
          <FaEye
            style={{ fontSize: 16, color: "#2484c6", cursor: "pointer" }}
            title="View & assign courses"
            onClick={() => navigate(`/associate-management/${record._id}`)}
          />
          <FaEdit
            style={{ fontSize: 16, color: "#2484c6", cursor: "pointer" }}
            title="Edit"
            onClick={() => openEditModal(record)}
          />
          <FaTrash
            style={{ fontSize: 16, color: "#c62828", cursor: "pointer" }}
            title="Delete"
            onClick={() => handleDelete(record)}
          />
        </div>
      ),
    },
  ];

  if (!canManage) {
    return (
      <Layout className="configuration" style={{ minHeight: "100vh" }}>
        <Row
          className="blueBackground-old coursebnr"
          style={{
            height: "45vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography.Title className="fontFamily1" style={{ fontSize: 30 }}>
            Associate management
          </Typography.Title>
        </Row>
        <Row style={{ justifyContent: "center", padding: 48 }}>
          <Card>
            <Typography.Paragraph>
              Training associates are not enabled on your account. Contact admin
              if you need this feature.
            </Typography.Paragraph>
            <Button type="primary" onClick={() => navigate("/dashboard")}>
              Back to dashboard
            </Button>
          </Card>
        </Row>
      </Layout>
    );
  }

  return (
    <Layout
      className="configuration upcoming-lessons-page"
      style={{ minHeight: "100vh" }}
    >
      <Row className="blueBackground-old coursebnr">
        <Typography.Title
          level={1}
          className="fontFamily1 aboutbanner-content upcoming-lessons-hero-title"
        >
          Associate management
        </Typography.Title>
      </Row>

      <div className="boxDetails">
        <Row
          className="whiteBackground upcoming-lessons-section"
          style={{ justifyContent: "center" }}
        >
          <Col xs={24} lg={22} xl={21}>
            <div className="upcoming-lessons-inner">
              <Card className="infoBox upcoming-lessons-card" bordered={false}>
                <Row className="upcoming-lessons-toolbar" gutter={[16, 16]}>
                  <Col xs={24} md={14}>
                    <div className="course-management-title-block">
                      <button
                        type="button"
                        className="course-management-inline-back"
                        aria-label="Go back"
                        onClick={() => navigate("/dashboard")}
                      >
                        <FaArrowLeft aria-hidden="true" />
                      </button>
                      <Typography.Title
                        level={2}
                        className="fontFamily1 course-management-page-title"
                      >
                        Training associates
                      </Typography.Title>
                    </div>
                  </Col>
                  <Col xs={24} md={10} className="quiz-management-actions-col">
                    <div className="course-management-primary-actions">
                      <Button
                        type="primary"
                        shape="round"
                        size="large"
                        className="loginButton"
                        onClick={openAddModal}
                      >
                        Add associate
                      </Button>
                    </div>
                  </Col>
                </Row>

                {loading ? (
                  <Skeleton active />
                ) : associates.length === 0 ? (
                  <Empty description="No associates yet" />
                ) : (
                  <Table
                    rowKey="_id"
                    columns={columns}
                    dataSource={associates}
                    pagination={false}
                    className="styledTable2"
                  />
                )}
              </Card>
            </div>
          </Col>
        </Row>
      </div>

      <Modal
        title={editMode ? "Edit associate" : "Add associate"}
        open={modalOpen}
        onCancel={closeModal}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <Form.Item
            label="First name"
            name="firstName"
            rules={[{ required: true, message: "First name is required" }]}
          >
            <Input size="large" placeholder="First name" />
          </Form.Item>
          <Form.Item
            label="Last name"
            name="lastName"
            rules={[{ required: true, message: "Last name is required" }]}
          >
            <Input size="large" placeholder="Last name" />
          </Form.Item>
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Email is required" },
              { type: "email", message: "Enter a valid email" },
            ]}
          >
            <Input size="large" placeholder="Email" />
          </Form.Item>
          <Form.Item
            label="Position"
            name="position"
            rules={[{ required: true, message: "Position is required" }]}
          >
            <Input size="large" placeholder="e.g. Training coordinator" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Button onClick={closeModal} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={saving}>
              {editMode ? "Save changes" : "Add associate"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
}

export default AssociateManagement;
